import { StarFilled } from '@ant-design/icons';
import campaignsCRUDActions from '@mm/state/actions/CampaignsCRUDActions';
import MessengerAdCRUDActions from '@mm/state/actions/MessengerAdCRUDActions';
import { CampaignType } from '@mm/state/model/CampaignsModel';
import {
  associatedAudienceSelector,
  associatedCampaignSelector,
} from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as StarIcon } from 'assets/star.svg';
import classnames from 'classnames';
import { isArray } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import Checkbox from '../../../../../common/components/Checkbox';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { ErrorTooltip } from '../../../../../common/components/ErrorTooltip/ErrorTooltip';
import TextInput from '../../../../../common/components/TextInput';

type OuterProps = {
  messengerAdId: number;
  collapsed: boolean;
  usedInSlideout?: boolean;
  orderNumber?: string;
};

export const SetupDripCampaign = (props: OuterProps) => {
  const { messengerAdId, collapsed, usedInSlideout, orderNumber } = props;

  const botId = useSelector(activeChatbotIdSelector);
  const associatedAudience = useSelector(associatedAudienceSelector);
  const associatedCampaign = useSelector(associatedCampaignSelector);

  const dispatch: Dispatch<StoreState> = useDispatch();

  const updateAudienceName = (
    botId: number,
    messengerAdId: number,
    audienceName: string,
  ) =>
    dispatch(
      MessengerAdCRUDActions.updateAudienceName(
        botId,
        messengerAdId,
        audienceName,
      ),
    );
  const destroyAudience = (
    botId: number,
    messengerAdId: number,
    audienceId: number,
  ) =>
    dispatch(
      MessengerAdCRUDActions.destroyAudience(botId, messengerAdId, audienceId),
    );
  const createAudience = (botId: number, messengerAdId: number) =>
    dispatch(MessengerAdCRUDActions.createAudience(botId, messengerAdId));

  const updateCampaign = (campaign: CampaignType) =>
    dispatch(campaignsCRUDActions.update(campaign, campaign.id));

  const [audienceName, setAudienceName] = useState('');
  const [remoteValidation, setRemoteValidation] = useState<string[]>([]);

  useEffect(() => {
    setAudienceName(associatedAudience?.name || '');
  }, [associatedAudience]);

  const changeAudienceName = (value: string) => {
    setAudienceName(value);
  };

  const onUseAudience = async (checked: boolean) => {
    if (checked) {
      await createAudience(botId, messengerAdId);
    } else if (associatedAudience) {
      await unlinkAssociatedCampaign();
      await destroyAudience(botId, messengerAdId, associatedAudience.id);
    }
  };

  const unlinkAssociatedCampaign = () => {
    if (
      associatedCampaign &&
      associatedCampaign.campaignAudiencesAttributes &&
      associatedAudience
    ) {
      const campaignAudiencesAttributes =
        associatedCampaign.campaignAudiencesAttributes.map(a =>
          a.audienceId === associatedAudience.id ? { ...a, destroy: true } : a,
        );
      return updateCampaign({
        ...associatedCampaign,
        campaignAudiencesAttributes,
      });
    }
  };

  const saveAudienceName = async () => {
    const resultOrError = await updateAudienceName(
      botId,
      messengerAdId,
      audienceName,
    );
    if (isArray(resultOrError)) {
      setRemoteValidation(resultOrError);
    } else {
      setRemoteValidation([]);
    }
  };

  const containerName = !usedInSlideout ? (
    'Advanced'
  ) : (
    <div className="messenger-ad-detail__advanced-header-wrapper">
      Advanced
      <StarFilled className="messenger-ad-detail__advanced-header-icon" />
    </div>
  );
  return (
    <div
      className={classnames('messenger-ad-detail__advanced', {
        'messenger-ad-detail__advanced-slideout': usedInSlideout,
      })}
    >
      {!usedInSlideout && (
        <StarIcon className="messenger-ad-detail__advanced-icon" />
      )}
      <CollapsibleContentContainer
        disableExpand={collapsed}
        containerName={containerName}
        className={
          !usedInSlideout
            ? 'ad-detail__form-step-collapsible-content'
            : 'ad-detail__slideout-step-container'
        }
        useCircleNumberStyle={usedInSlideout}
        orderNumber={orderNumber}
        stepperStylingPlatform="facebook"
      >
        <div className="ad-detail__section-container ad-details-setup-drip-campaign">
          <Checkbox
            className="messenger-ad-detail__audience-checkbox"
            labelClassName="messenger-ad-detail__audience-checkbox-label"
            isChecked={Boolean(associatedAudience)}
            onToggle={e => onUseAudience(e.target.checked)}
            label={
              <div className="messenger-ad-detail__audience-label">
                Create audience from contacts generated by this ad
              </div>
            }
          />
          {associatedAudience && (
            <div className="ad-detail__inputs-container">
              <div
                className={classnames(
                  'ad-detail__input-container',
                  'ad-detail__advanced-options',
                  {
                    'ad-detail__input-container-slideout': usedInSlideout,
                  },
                )}
              >
                <div className="ad-detail__field-title">Name</div>
                <div className="ad-detail__text-input-container">
                  <TextInput
                    className="ad-detail__input messenger-ad-detail__audience-name-input"
                    value={audienceName}
                    onChange={e => changeAudienceName(e.target.value)}
                  />
                  <PrimaryButton
                    className="messenger-ad-detail__audience-name-button"
                    onClick={saveAudienceName}
                    disabled={
                      audienceName === associatedAudience?.name || !audienceName
                    }
                  >
                    Update
                  </PrimaryButton>
                  <ErrorTooltip
                    classPrefix="ad-detail"
                    validation={remoteValidation}
                    hoverMode
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </CollapsibleContentContainer>
    </div>
  );
};
